<i18n>
[
    "a11y__carousel--play",
    "a11y__carousel--pause",
    "a11y__carousel--nextSlide",
    "a11y__carousel--previousSlide",
    "a11y__carousel--slideCount",
]
</i18n>

<template>
    <div
        ref="containerRef"
        class="c-auto-rotate-slider"
    >
        <div class="c-auto-rotate-slider__container">
            <div
                ref="slidesContainerRef"
                class="c-auto-rotate-slider__slides-container"
                :aria-live="isActive ? 'off' : 'polite'"
            >
                <div
                    v-for="(slide, index) in slides"
                    :key="slide.id"
                    role="group"
                    class="c-auto-rotate-slider__slide"
                    :class="{ 'c-auto-rotate-slider__slide--active': index === currentSlideIndex }"
                    :aria-hidden="index !== currentSlideIndex"
                    :aria-label="t('a11y__carousel--slideCount', [index + 1, numberOfSlides])"
                >
                    <ImageModule
                        :key="slide.title"
                        :backgroundImage="slide.backgroundImage"
                        :buttons="slide.buttons"
                        :destination="slide.destination"
                        :editorialText="slide.editorialText"
                        :tabIndex="index === currentSlideIndex ? 0 : -1"
                    />
                </div>
            </div>
            <template v-if="numberOfSlides > 1">
                <PaginationButton
                    :direction="BACKWARD"
                    :label="t('a11y__carousel--previousSlide')"
                    class="c-auto-rotate-slider__pagination-button c-auto-rotate-slider__pagination-button--backward"
                    @click="goToPreviousSlide"
                />
                <PaginationButton
                    :direction="FORWARD"
                    :label="t('a11y__carousel--nextSlide')"
                    class="c-auto-rotate-slider__pagination-button c-auto-rotate-slider__pagination-button--forward"
                    @click="goToNextSlide"
                />
                <div
                    v-tab-focus.onspace="toggleAutoplay"
                    class="c-auto-rotate-slider__play-pause-container"
                    :aria-label="isActive ? t('a11y__carousel--pause') : t('a11y__carousel--play')"
                >
                    <MediaButton
                        :activeButtonAction="isActive ? MEDIA_BUTTON_ACTIONS.pause : MEDIA_BUTTON_ACTIONS.play"
                        :hideText="true"
                        class="c-auto-rotate-slider__play-pause-button"
                    />
                </div>
            </template>
        </div>
        <SliderDots
            v-if="numberOfSlides > 1"
            :pageCount="numberOfSlides"
            :pageIndex="currentSlideIndex"
            :name="name"
            class="c-auto-rotate-slider__slider-dots"
            @change-page="goToSlideIndex"
        />
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAutoplay } from '~coreModules/core/js/composables/useAutoplay';

import { FORWARD, BACKWARD } from '~coreModules/core/js/constants';
import { MEDIA_BUTTON_ACTIONS } from '~coreModules/core/js/media-constants';

import ImageModule from '~coreModules/contentful/components/ImageModule.vue';
import MediaButton from '~coreModules/core/components/ui/buttons/MediaButton.vue';
import PaginationButton from '~coreModules/core/components/ui/buttons/PaginationButton.vue';
import SliderDots from '~coreModules/core/components/ui/SliderDots.vue';

const props = defineProps({
    slides: {
        type: Array,
        required: true,
    },
    name: {
        type: String,
        required: true,
    },
});

const { t } = useI18n();

const containerRef = ref(null);
const slidesContainerRef = ref(null);

const numberOfSlides = computed(() => props.slides.length);

const {
    isActive,
    currentSlideIndex,
    toggleAutoplay,
    changePage,
    goToSlideIndex,
} = useAutoplay(
    containerRef,
    slidesContainerRef,
    numberOfSlides.value,
);

function handleManualNavigation(navigationDirection) {
    changePage(navigationDirection, true);
}

function goToPreviousSlide() {
    handleManualNavigation(BACKWARD);
}

function goToNextSlide() {
    handleManualNavigation(FORWARD);
}
</script>

<style lang="scss">
.c-auto-rotate-slider {
    $this: &;

    &__container {
        position: relative;
        margin-bottom: $nu-spacer-2;

        @include breakpoint(small) {
            margin-bottom: $nu-spacer-3;
        }

        &:focus-within,
        &:hover {
            #{$this}__pagination-button {
                opacity: 0.8;
            }
        }
    }

    &__slides-container {
        position: relative;
        overflow: hidden;
    }

    &__slide {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        opacity: 0;
        transition: opacity 0.8s ease-in-out;
        pointer-events: none;

        &--active {
            opacity: 1;
            pointer-events: auto;
        }

        // Set height for first slide to establish container height
        &:first-child {
            position: relative;
            opacity: 1;
        }

        &:first-child:not(#{$this}__slide--active) {
            opacity: 0;
        }

        &:first-child#{$this}__slide--active {
            opacity: 1;
        }
    }

    &__pagination-button {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        z-index: 2;

        @include touch-device {
            display: none;

            @include breakpoint(large) {
                display: flex;
            }
        }

        @include non-touch-device {
            transition: opacity 0.2s ease;
            opacity: 0;
        }

        &--backward {
            left: $nu-spacer-10;
        }

        &--forward {
            right: $nu-spacer-10;
        }
    }

    &__play-pause-container {
        position: absolute;
        bottom: $nu-spacer-1;
        right: $nu-spacer-1;
        z-index: 2;
        padding: $nu-spacer-0pt5;

        @include breakpoint(small) {
            bottom: $nu-spacer-2;
            right: $nu-spacer-2;
        }
    }
}
</style>
